const productPaths = require('./product-paths.cjs')
const structuredData = require('./structured-data.cjs')

const {
  ORGANIZATION_ID,
  SITE_URL,
  WEBSITE_ID,
  absoluteUrl,
  buildBreadcrumbNode,
  canonicalUrl,
  extractText,
  getOpenGraphImage,
  getPageDescription,
  getPageTitle,
  getSiteNodes,
  normalizeText,
  pruneEmpty,
} = structuredData

const toArray = (value) =>
  Array.isArray(value) ? value : value == null ? [] : [value]

const slugify = (value) =>
  normalizeText(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

const ORGANIZATION_CANONICAL_PATHS = new Map([
  [
    'c-worldwide-asset-management',
    '/investment-teams/c-worldwide-asset-management/',
  ],
  ['income-research-management', '/investment-teams/ir-m/'],
  ['jennison-associates', '/investment-teams/jennison-associates-llc/'],
  ['jennison-associates-llc', '/investment-teams/jennison-associates-llc/'],
  [
    'osmosis-investment-management',
    '/investment-teams/osmosis-investment-management-us-llc-/',
  ],
  [
    'osmosis-investment-management-us-llc',
    '/investment-teams/osmosis-investment-management-us-llc-/',
  ],
  ['quantix-commodities', '/investment-teams/quantix-commodities-lp/'],
  ['quantix-commodities-l-p', '/investment-teams/quantix-commodities-lp/'],
  ['quantix-commodities-lp', '/investment-teams/quantix-commodities-lp/'],
  [
    'westfield-management-company-l-p',
    '/investment-teams/westfield-capital-management/',
  ],
  [
    'westfield-capital-management-company-l-p',
    '/investment-teams/westfield-capital-management/',
  ],
])

const getOrganizationIdentityKey = (value) => {
  const key = slugify(value)
  const canonicalPath = ORGANIZATION_CANONICAL_PATHS.get(key)
  return canonicalPath
    ? slugify(canonicalPath.replace(/^\/investment-teams\//, ''))
    : key
}

const getOrganizationCanonicalPath = (organization = {}) => {
  const name = organization?.title || organization?.name
  const configuredPath = ORGANIZATION_CANONICAL_PATHS.get(slugify(name))
  if (configuredPath) return configuredPath

  const urlPath = organization?.url
    ? structuredData.normalizePathname(organization.url)
    : ''
  return urlPath.startsWith('/investment-teams/') ? urlPath : ''
}

const isHarborOrganizationName = (value) => {
  const normalized = normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
  return (
    normalized === 'harbor capital advisors' ||
    normalized === 'harbor capital advisors inc'
  )
}

const getAssetUrl = (asset) =>
  absoluteUrl(
    asset?.localAsset?.publicURL ||
      asset?.localAsset?.childImageSharp?.resize?.src ||
      asset?.url
  )

const getEntityImage = (entity = {}) =>
  getAssetUrl(
    entity?.default_photo ||
      entity?.photo ||
      entity?.leadership?.leadership_photo ||
      entity?.logo ||
      entity?.alternate_logo
  )

const getPersonId = (person = {}) => {
  const key =
    slugify(person?.uid) ||
    slugify(person?.url) ||
    slugify(person?.id) ||
    slugify(person?.title)
  return key ? `${SITE_URL}#person-${key}` : ''
}

const getOrganizationId = (
  organization = {},
  { preferHarborRoot = true } = {}
) => {
  const name = organization?.title || organization?.name
  if (preferHarborRoot && isHarborOrganizationName(name)) {
    return ORGANIZATION_ID
  }
  const canonicalPath = getOrganizationCanonicalPath(organization)
  if (canonicalPath) return `${canonicalUrl(canonicalPath)}#organization`

  const key =
    getOrganizationIdentityKey(name) ||
    slugify(organization?.uid) ||
    slugify(organization?.id)
  return key ? `${SITE_URL}#organization-${key}` : ''
}

const getAuthorProfilePath = (entity = {}) =>
  entity?.url ? `/insights/author${entity.url}` : ''

const getTeamMemberProfilePath = (entity = {}) =>
  entity?.url ? `/subadvisor${entity.url}` : ''

const getLeadershipProfilePath = (entity = {}) =>
  entity?.url ? `/leadership${entity.url}` : ''

const isOrganizationProfile = (entity = {}) => {
  const name = normalizeText(entity?.title || entity?.name)
  if (isHarborOrganizationName(name)) return true
  if (
    entity?.author_type === 'CURRENT_HARBOR_EMPLOYEE' ||
    normalizeText(entity?.company) ||
    normalizeText(entity?.job_title) ||
    entity?.leadership
  ) {
    return false
  }
  if (
    /\b(advisors?|asset management|investment management|capital management|research(?:\s*\+\s*|\s+and\s+)?management|commodities|partners?|company|team|llc|l\.?p\.?|inc\.?)\b/i.test(
      name
    )
  ) {
    return true
  }

  return false
}

const buildOrganizationNode = (
  organization = {},
  { profilePath, preferHarborRoot = true } = {}
) => {
  const name = normalizeText(organization?.title || organization?.name)
  const isHarborRoot = preferHarborRoot && isHarborOrganizationName(name)
  const canonicalPath = getOrganizationCanonicalPath(organization)
  const organizationId = isHarborRoot
    ? ORGANIZATION_ID
    : getOrganizationId(organization, { preferHarborRoot: false })
  if (!name || !organizationId) return null

  return pruneEmpty({
    '@type': 'Organization',
    '@id': organizationId,
    name,
    legalName: normalizeText(organization?.legalName),
    url: isHarborRoot
      ? SITE_URL
      : canonicalPath
        ? canonicalUrl(canonicalPath)
        : profilePath
          ? canonicalUrl(profilePath)
          : organization?.url
            ? canonicalUrl(organization.url)
            : undefined,
    description:
      extractText(organization?.description) ||
      extractText(organization?.short_description) ||
      extractText(organization?.bio),
    logo: getEntityImage(organization)
      ? {
          '@type': 'ImageObject',
          url: getEntityImage(organization),
        }
      : undefined,
  })
}

const getReferencedTeam = (person = {}) =>
  toArray(person?.product_subadvisers)
    .flatMap((item) => toArray(item?.subadviser_firm))
    .find((firm) => normalizeText(firm?.title))

const getCompanyOrganization = (person = {}) => {
  if (
    person?.author_type === 'CURRENT_HARBOR_EMPLOYEE' ||
    isHarborOrganizationName(person?.company)
  ) {
    return {
      title: 'Harbor Capital Advisors, Inc.',
      url: '/',
    }
  }

  return normalizeText(person?.company)
    ? { title: normalizeText(person.company) }
    : null
}

const buildPersonRelationships = (person = {}, explicitTeam) => {
  const referencedTeam = explicitTeam || getReferencedTeam(person)
  const company = getCompanyOrganization(person)
  const teamNode = referencedTeam
    ? buildOrganizationNode(referencedTeam, {
        preferHarborRoot: isHarborOrganizationName(referencedTeam?.title),
      })
    : null
  const companyNode = company ? buildOrganizationNode(company) : null
  const companyMatchesTeam =
    companyNode &&
    teamNode &&
    normalizeText(company?.title)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') ===
      normalizeText(referencedTeam?.title)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
  const worksForId =
    companyNode?.['@id'] === ORGANIZATION_ID
      ? ORGANIZATION_ID
      : companyMatchesTeam
        ? teamNode?.['@id']
        : companyNode?.['@id']

  return {
    nodes: [
      companyMatchesTeam || companyNode?.['@id'] === ORGANIZATION_ID
        ? null
        : companyNode,
      teamNode?.['@id'] === ORGANIZATION_ID ? null : teamNode,
    ].filter(Boolean),
    worksFor: worksForId ? { '@id': worksForId } : undefined,
    affiliation: teamNode ? { '@id': teamNode['@id'] } : undefined,
  }
}

const buildPersonNode = (person = {}, { profilePath, organization } = {}) => {
  const name = normalizeText(person?.title || person?.name)
  const personId = getPersonId(person)
  if (!name || !personId) return { node: null, relatedNodes: [] }

  const relationships = buildPersonRelationships(person, organization)
  const description =
    extractText(person?.leadership?.leadership_bio) || extractText(person?.bio)

  return {
    node: pruneEmpty({
      '@type': 'Person',
      '@id': personId,
      name,
      honorificPrefix: normalizeText(person?.title_prefix),
      honorificSuffix: normalizeText(person?.title_suffix),
      url: profilePath ? canonicalUrl(profilePath) : undefined,
      jobTitle:
        normalizeText(person?.leadership?.leadership_title) ||
        normalizeText(person?.job_title),
      description,
      image: getEntityImage(person),
      worksFor: relationships.worksFor,
      affiliation: relationships.affiliation,
    }),
    relatedNodes: relationships.nodes,
  }
}

const buildAuthorshipEntity = (entity = {}, profilePath) => {
  if (isOrganizationProfile(entity)) {
    const organization = buildOrganizationNode(entity, {
      profilePath,
    })
    return {
      node: organization,
      relatedNodes: [],
    }
  }

  return buildPersonNode(entity, { profilePath })
}

const mergeGraphNodes = (nodes) => {
  const keyed = new Map()
  const unkeyed = []

  toArray(nodes)
    .flat()
    .filter(Boolean)
    .forEach((node) => {
      const id = node?.['@id']
      if (!id) {
        unkeyed.push(node)
        return
      }
      keyed.set(id, { ...(keyed.get(id) || {}), ...node })
    })

  return [...keyed.values(), ...unkeyed]
}

const buildArticleBreadcrumbItems = ({ article, pathname, articleType }) => [
  { name: 'Home', path: '/' },
  {
    name: articleType === 'NewsArticle' ? 'News' : 'Insights',
    path: articleType === 'NewsArticle' ? '/about-us/news/' : '/insights/',
  },
  { name: article?.hero_title || article?.title, path: pathname },
]

const getRelatedProductNodes = (article = {}) =>
  toArray(article?.related_products)
    .map((product) => {
      const path = productPaths.getProductPath(product) || product?.url
      const url = path ? canonicalUrl(path) : ''
      const name = normalizeText(product?.title)
      if (!url || !name) return null

      return {
        '@type': 'InvestmentFund',
        '@id': `${url}#investment-fund`,
        name,
        url,
        provider: { '@id': ORGANIZATION_ID },
      }
    })
    .filter(Boolean)

const buildArticleGraph = ({
  article = {},
  pathname,
  articleType = 'Article',
  reviewers = [],
  breadcrumbItems,
} = {}) => {
  const canonical = canonicalUrl(pathname)
  const webpageId = `${canonical}#webpage`
  const articleId = `${canonical}#article`
  const visibleAuthors = toArray(article?.people)
  const authors = (
    visibleAuthors.length ? visibleAuthors : toArray(article?.authors)
  )
    .map((author) =>
      buildAuthorshipEntity(author, getAuthorProfilePath(author))
    )
    .filter((result) => result.node)
  const explicitReviewerEntities = toArray(reviewers)
    .map((reviewer) =>
      buildAuthorshipEntity(reviewer, getAuthorProfilePath(reviewer))
    )
    .filter((result) => result.node)
  const hasCompleteReview =
    explicitReviewerEntities.length > 0 && Boolean(article?.reviewed_at)
  const reviewerEntities = hasCompleteReview ? explicitReviewerEntities : []
  const relatedProducts = getRelatedProductNodes(article)
  const breadcrumb = buildBreadcrumbNode(
    breadcrumbItems ||
      buildArticleBreadcrumbItems({ article, pathname, articleType }),
    pathname
  )
  const image =
    getOpenGraphImage(article?.open_graph) || getAssetUrl(article?.panel_image)
  const description =
    getPageDescription(article) || extractText(article?.teaser)
  const authorRefs = authors.map(({ node }) => ({ '@id': node['@id'] }))
  const reviewerRefs = reviewerEntities.map(({ node }) => ({
    '@id': node['@id'],
  }))
  const articleSections = toArray(article?.insight_categories)
    .map((category) => normalizeText(category?.title))
    .filter(Boolean)

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@graph': mergeGraphNodes([
      ...getSiteNodes(),
      {
        '@type': 'WebPage',
        '@id': webpageId,
        url: canonical,
        name: getPageTitle(article),
        description,
        isPartOf: { '@id': WEBSITE_ID },
        mainEntity: { '@id': articleId },
        breadcrumb: { '@id': breadcrumb['@id'] },
        reviewedBy: hasCompleteReview ? reviewerRefs : undefined,
        lastReviewed: hasCompleteReview ? article.reviewed_at : undefined,
        dateModified: article?.updated_at,
        inLanguage: 'en-US',
        primaryImageOfPage: image
          ? { '@type': 'ImageObject', url: image }
          : undefined,
      },
      {
        '@type': articleType,
        '@id': articleId,
        url: canonical,
        mainEntityOfPage: { '@id': webpageId },
        headline: normalizeText(article?.hero_title || article?.title),
        alternativeHeadline:
          article?.hero_title && article?.title !== article?.hero_title
            ? normalizeText(article.title)
            : undefined,
        description,
        image,
        datePublished: article?.hide_date ? undefined : article?.date,
        dateModified:
          article?.date_modified ||
          (hasCompleteReview ? article.reviewed_at : undefined),
        author: authorRefs,
        publisher: { '@id': ORGANIZATION_ID },
        articleSection: articleSections,
        mentions: relatedProducts.map((product) => ({
          '@id': product['@id'],
        })),
        inLanguage: 'en-US',
      },
      breadcrumb,
      authors.flatMap(({ node, relatedNodes }) => [node, ...relatedNodes]),
      reviewerEntities.flatMap(({ node, relatedNodes }) => [
        node,
        ...relatedNodes,
      ]),
      relatedProducts,
    ]),
  })
}

const buildProfileGraph = ({
  entity = {},
  pathname,
  profileType = 'author',
  organization,
  breadcrumbItems,
} = {}) => {
  const canonical = canonicalUrl(pathname)
  const webpageId = `${canonical}#webpage`
  const isOrganization = isOrganizationProfile(entity)
  const profilePath = pathname
  const entityResult = isOrganization
    ? {
        node: buildOrganizationNode(entity, { profilePath }),
        relatedNodes: [],
      }
    : buildPersonNode(entity, { profilePath, organization })
  if (!entityResult.node) return null

  const parentName =
    profileType === 'leadership'
      ? 'Leadership'
      : profileType === 'investment-team-member'
        ? 'Investment Teams'
        : 'Authors'
  const parentPath =
    profileType === 'leadership'
      ? '/about-us/leadership/'
      : profileType === 'investment-team-member'
        ? organization?.url || '/about-us/investment-teams/'
        : '/insights/'
  const breadcrumb = buildBreadcrumbNode(
    breadcrumbItems || [
      { name: 'Home', path: '/' },
      { name: parentName, path: parentPath },
      { name: entity?.title, path: pathname },
    ],
    pathname
  )
  const description =
    extractText(entity?.leadership?.leadership_bio) ||
    extractText(entity?.bio) ||
    extractText(entity?.description)

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@graph': mergeGraphNodes([
      ...getSiteNodes(),
      {
        '@type': 'ProfilePage',
        '@id': webpageId,
        url: canonical,
        name: normalizeText(entity?.title),
        description,
        isPartOf: { '@id': WEBSITE_ID },
        mainEntity: { '@id': entityResult.node['@id'] },
        breadcrumb: { '@id': breadcrumb['@id'] },
        dateModified: entity?.updated_at,
        inLanguage: 'en-US',
        primaryImageOfPage: getEntityImage(entity)
          ? { '@type': 'ImageObject', url: getEntityImage(entity) }
          : undefined,
      },
      breadcrumb,
      entityResult.node,
      entityResult.relatedNodes,
    ]),
  })
}

const buildInvestmentTeamGraph = ({
  team = {},
  pathname,
  people = team?.people,
  breadcrumbItems,
} = {}) => {
  const canonical = canonicalUrl(pathname)
  const webpageId = `${canonical}#webpage`
  const isHarborTeam = isHarborOrganizationName(team?.title)
  const teamNode = buildOrganizationNode(team, {
    profilePath: pathname,
    preferHarborRoot: isHarborTeam,
  })
  if (!teamNode) return null
  if (isHarborTeam) {
    delete teamNode.description
    teamNode.url = SITE_URL
  }

  const members = toArray(people)
    .map((person) =>
      buildPersonNode(person, {
        profilePath: getTeamMemberProfilePath(person),
        organization: team,
      })
    )
    .filter((result) => result.node)
  teamNode.member = members.map(({ node }) => ({ '@id': node['@id'] }))
  const breadcrumb = buildBreadcrumbNode(
    breadcrumbItems || [
      { name: 'Home', path: '/' },
      { name: 'Investment Teams', path: '/about-us/investment-teams/' },
      { name: team?.title, path: pathname },
    ],
    pathname
  )

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@graph': mergeGraphNodes([
      ...getSiteNodes(),
      {
        '@type': 'ProfilePage',
        '@id': webpageId,
        url: canonical,
        name: normalizeText(team?.title),
        description: isHarborTeam ? undefined : getPageDescription(team),
        isPartOf: { '@id': WEBSITE_ID },
        mainEntity: { '@id': teamNode['@id'] },
        breadcrumb: { '@id': breadcrumb['@id'] },
        dateModified: team?.updated_at,
        inLanguage: 'en-US',
      },
      breadcrumb,
      teamNode,
      members.flatMap(({ node, relatedNodes }) => [node, ...relatedNodes]),
    ]),
  })
}

const buildEntityCollectionGraph = ({
  page = {},
  pathname,
  entities = [],
  entityType = 'organization',
  breadcrumbItems,
} = {}) => {
  const canonical = canonicalUrl(pathname)
  const webpageId = `${canonical}#webpage`
  const itemListId = `${canonical}#item-list`
  const normalizedEntities = toArray(entities)
    .map((entity) => {
      if (entityType === 'person') {
        return {
          ...buildPersonNode(entity, {
            profilePath: getLeadershipProfilePath(entity),
            organization: {
              title: 'Harbor Capital Advisors, Inc.',
              url: '/',
            },
          }),
          profileUrl: getLeadershipProfilePath(entity),
        }
      }
      return {
        node: buildOrganizationNode(entity, {
          profilePath: entity?.url,
          preferHarborRoot: isHarborOrganizationName(entity?.title),
        }),
        relatedNodes: [],
        profileUrl: entity?.url,
      }
    })
    .filter((result) => result.node)
  const breadcrumb = buildBreadcrumbNode(
    breadcrumbItems || [
      { name: 'Home', path: '/' },
      { name: page?.title, path: pathname },
    ],
    pathname
  )
  const listItems = normalizedEntities.map(({ node, profileUrl }, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: profileUrl ? canonicalUrl(profileUrl) : node.url,
    item: { '@id': node['@id'] },
  }))
  const siteNodes = getSiteNodes()
  const harborOrganization = siteNodes.find(
    (node) => node?.['@id'] === ORGANIZATION_ID
  )
  if (entityType === 'person' && harborOrganization) {
    harborOrganization.employee = normalizedEntities.map(({ node }) => ({
      '@id': node['@id'],
    }))
  }

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@graph': mergeGraphNodes([
      ...siteNodes,
      {
        '@type': 'CollectionPage',
        '@id': webpageId,
        url: canonical,
        name: getPageTitle(page),
        description: getPageDescription(page),
        isPartOf: { '@id': WEBSITE_ID },
        mainEntity: { '@id': itemListId },
        breadcrumb: { '@id': breadcrumb['@id'] },
        dateModified: page?.updated_at,
        inLanguage: 'en-US',
      },
      {
        '@type': 'ItemList',
        '@id': itemListId,
        name: `${normalizeText(page?.title) || 'People and organizations'} list`,
        numberOfItems: listItems.length,
        itemListElement: listItems,
      },
      breadcrumb,
      normalizedEntities.flatMap(({ node, relatedNodes }) => [
        node,
        ...relatedNodes,
      ]),
    ]),
  })
}

const buildSiteOrganizationGraph = ({ page = {}, pathname } = {}) => {
  const canonical = canonicalUrl(pathname)
  const webpageId = `${canonical}#webpage`

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@graph': mergeGraphNodes([
      ...getSiteNodes(),
      {
        '@type': pathname === '/' ? 'WebPage' : 'AboutPage',
        '@id': webpageId,
        url: canonical,
        name: getPageTitle(page),
        description: getPageDescription(page),
        isPartOf: { '@id': WEBSITE_ID },
        mainEntity: { '@id': ORGANIZATION_ID },
        dateModified: page?.updated_at,
        inLanguage: 'en-US',
      },
    ]),
  })
}

module.exports = {
  buildArticleGraph,
  buildEntityCollectionGraph,
  buildInvestmentTeamGraph,
  buildOrganizationNode,
  buildPersonNode,
  buildProfileGraph,
  buildSiteOrganizationGraph,
  getAuthorProfilePath,
  getLeadershipProfilePath,
  getOrganizationCanonicalPath,
  getOrganizationId,
  getOrganizationIdentityKey,
  getPersonId,
  getTeamMemberProfilePath,
  isHarborOrganizationName,
  isOrganizationProfile,
  mergeGraphNodes,
}
